import * as React from 'react'
import {
  Box,
  Button,
  Modal,
  ModalActions,
  ModalContent,
  ModalHeader,
  Text,
} from '@lightspeed/design-system-react'
import { ApiCustomer } from '@vend/types'
import { useT } from '@transifex/react'
import { StepBreadcrumbs } from '../StepBreadcrumbs'
import { CUSTOMER_STEP_VIEW, STEPS } from '../types'
import {
  hasContactInfo,
  isInvalidEmail,
} from '../../../common/customer-contact-utils'
import {
  CustomerInfo,
  PartialCustomerGroup,
  SOURCE_MODAL,
} from '../../../common/types'
import { SelectedCustomer } from '../../../common/SelectedCustomer'
import { CustomerSearch } from '../../../../../customer/customer-search/CustomerSearch'
import { AddNewCustomerView } from './AddNewCustomerView'

interface Props {
  onClose: (shouldCreate: boolean) => void
  onStepChange: (step: STEPS) => void
  onAdd: () => void
  onSelect: (customer: ApiCustomer | null) => void
  onContactChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  selectedCustomer: ApiCustomer | null
  newCustomer: CustomerInfo | null
  group?: PartialCustomerGroup
  isEditing?: boolean
  customerStepView: CUSTOMER_STEP_VIEW
  setCustomerStepView: (view: CUSTOMER_STEP_VIEW) => void
}

export const StepSelectCustomer: React.FC<Props> = ({
  onClose,
  onStepChange,
  onAdd,
  onSelect,
  onContactChange,
  selectedCustomer,
  newCustomer,
  group,
  isEditing,
  customerStepView,
  setCustomerStepView,
}) => {
  const [
    shouldDisplayAuxiliaryBackButton,
    setShouldDisplayAuxiliaryBackButton,
  ] = React.useState(false)

  const shouldDisableNext = (customer: ApiCustomer | CustomerInfo | null) => {
    return (
      Boolean(!customer) ||
      !hasContactInfo(customer) ||
      isInvalidEmail(customer)
    )
  }

  const handleSelectExistingCustomer = () => {
    setCustomerStepView(CUSTOMER_STEP_VIEW.SELECT_CUSTOMER)
  }

  const t = useT()
  const stepSelectCustomerCopies = {
    addACustomer: t('Add a customer and their contact information', {
      _context: 'Header for customer step in service modal.',
    }),
    contactDescription: t(
      'Contact information will be used to notify the customer when their service is completed',
      {
        _context: 'Contact description for customer step in service modal.',
      }
    ),
    selectOrAddCustomer: t('Select or add a customer', {
      _context:
        'Customer search input placeholder text for customer step in service modal.',
    }),
    addExistingCustomer: t('Add an existing customer', {
      _context:
        'Description to add customer for customer step in service modal.',
    }),
    doNotUpdateService: t(`Don’t update service`, {
      _context: `Don't update button text for customer step in service modal.`,
    }),
    doNotCreateService: t(`Don’t create service`, {
      _context: `Don't create button text for customer step in service modal.`,
    }),
    next: t('Next', {
      _context: 'Next button text for customer step in service modal.',
    }),
  }

  const renderContent = () => {
    switch (customerStepView) {
      case CUSTOMER_STEP_VIEW.SELECT_CUSTOMER:
        if (shouldDisplayAuxiliaryBackButton) {
          setShouldDisplayAuxiliaryBackButton(false)
        }
        return selectedCustomer ? (
          <SelectedCustomer
            selectedCustomer={selectedCustomer}
            group={group}
            onContactChange={onContactChange}
            onSelect={onSelect}
            sourceModal={SOURCE_MODAL.SERVICE}
          />
        ) : (
          <CustomerSearch
            placeholder={stepSelectCustomerCopies.selectOrAddCustomer}
            onAdd={onAdd}
            onSelect={onSelect}
            searchValue={(newCustomer && newCustomer.firstName) || undefined}
          />
        )
      case CUSTOMER_STEP_VIEW.ADD_NEW_CUSTOMER:
        if (!shouldDisplayAuxiliaryBackButton) {
          setShouldDisplayAuxiliaryBackButton(true)
        }
        return (
          <AddNewCustomerView
            onChange={onContactChange}
            newCustomer={newCustomer}
          />
        )
    }
  }

  return (
    <Modal onClose={() => onClose(false)}>
      <StepBreadcrumbs step={STEPS.SELECT_CUSTOMER} />
      <ModalHeader>
        {stepSelectCustomerCopies.addACustomer}
        <Text tag="p" className="vd-mt2">
          {stepSelectCustomerCopies.contactDescription}
        </Text>
      </ModalHeader>
      <ModalContent>{renderContent()}</ModalContent>
      <ModalActions
        className={
          shouldDisplayAuxiliaryBackButton
            ? 'vd-flex vd-flex--justify-between vd-flex--align-center'
            : ''
        }
      >
        {shouldDisplayAuxiliaryBackButton && (
          <Button
            link
            data-track="back-add-existing-customer"
            onClick={handleSelectExistingCustomer}
          >
            {stepSelectCustomerCopies.addExistingCustomer}
          </Button>
        )}
        <Box className="vd-btn-group">
          <Button
            variant="supplementary"
            onClick={() => onClose(false)}
            data-cy="button-back"
            data-testid="cancel-update-or-create-service"
            data-track="cancel-update-or-create-service"
          >
            {isEditing
              ? stepSelectCustomerCopies.doNotUpdateService
              : stepSelectCustomerCopies.doNotCreateService}
          </Button>
          <Button
            variant="go"
            onClick={() => onStepChange(STEPS.CUSTOMER_ITEM)}
            disabled={
              customerStepView === CUSTOMER_STEP_VIEW.ADD_NEW_CUSTOMER
                ? shouldDisableNext(newCustomer)
                : shouldDisableNext(selectedCustomer)
            }
            data-cy="button-next"
            data-track="next-customer-item"
          >
            {stepSelectCustomerCopies.next}
          </Button>
        </Box>
      </ModalActions>
    </Modal>
  )
}

StepSelectCustomer.displayName = 'StepSelectCustomer'
